<script setup lang="ts">
import { ref, watch } from 'vue'
import {
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group'

export interface ToggleOption {
  value: string
  label: string
  badge?: string
}

const props = withDefaults(defineProps<{
  modelValue: string
  options: ToggleOption[]
  size?: 'sm' | 'md' | 'lg'
  containerClass?: string
  itemClass?: string
}>(), {
  size: 'md',
  containerClass: '',
  itemClass: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const selectedValue = ref(props.modelValue)

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  selectedValue.value = newValue
})

function handleValueChange(value: any) {
  if (value && typeof value === 'string') {
    selectedValue.value = value
    emit('update:modelValue', value)
  }
}

// Size-based classes (following Figma specs)
const itemClasses = {
  sm: 'px-3 py-1 text-xs min-w-min h-[28px]',
  md: 'px-4 py-1.5 text-sm min-w-min h-[33px]',
  lg: 'px-5 py-2 text-base min-w-min h-[38px]',
}
</script>

<template>
  <ToggleGroup
    type="single"
    :model-value="selectedValue"
    variant="outline"
    :spacing="0"
    :class="containerClass"
    @update:model-value="handleValueChange"
  >
    <ToggleGroupItem
      v-for="option in options"
      :key="option.value"
      :value="option.value"
      class="font-medium text-neutral-800 snap-start data-[state=on]:font-semibold data-[state=off]:bg-(image:--gradient-putih) data-[state=on]:bg-[#F3F4F6]"
      :class="[
        itemClasses[size],
        itemClass,
      ]"
    >
      <span class="inline-flex items-center gap-1">
        {{ option.label }}
        <span v-if="option.badge" class="rounded bg-pink-500 px-1 py-0.5 text-[10px] font-bold leading-none text-white">{{ option.badge }}</span>
        <slot :name="`extra-${option.value}`" :option="option" :is-active="selectedValue === option.value" />
      </span>
    </ToggleGroupItem>
  </ToggleGroup>
</template>
